import { useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Alert,
  Button,
  CircularProgress,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify"

import api from "../../api";
import InputField from "../../components/InputField";
import SubmitButton from "../../components/SubmitButton";

const Setting = () => {
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [loadingPin, setLoadingPin] = useState(false);

  const profileForm = useForm({
    defaultValues: { first_name: "", last_name: "" },
  });
  const passwordForm = useForm({
    defaultValues: {
      current_password: "",
      new_password: "",
      new_password_confirmation: "",
    },
  });
  const pinForm = useForm({
    defaultValues: {
      current_pin: "",
      new_pin: "",
      new_pin_confirmation: "",
    },
  });

  // Submit name changes
  const onUpdateProfile = async (data) => {
    setLoadingProfile(true);
    try {
      const payload = {
        first_name: data.first_name,
        last_name: data.last_name,
      };
      await api.post("/update-profile", payload);
      toast.success("Profile updated successfully!");
    } catch (err) {
      toast.error("Failed to update profile");
    } finally {
      setLoadingProfile(false);
    }
  };

  // Submit password change
  const onChangePassword = async (data) => {
    setLoadingPassword(true);
    try {
      const payload = {
        current_password: data.current_password,
        new_password: data.new_password,
        new_password_confirmation: data.new_password_confirmation,
      };
      await api.post("parent/change-password", payload);
      toast.success("Password changed successfully!");
      localStorage.removeItem("mustChangePassword");
      const userDataStr = localStorage.getItem("userData");
      if (userDataStr) {
        try {
          const parsed = JSON.parse(userDataStr);
          parsed.must_change_password = false;
          localStorage.setItem("userData", JSON.stringify(parsed));
        } catch (e) { /* ignore */ }
      }
      passwordForm.reset({
        current_password: "",
        new_password: "",
        new_password_confirmation: "",
      });
    } catch (err) {
      const backendErrors = err.response?.data?.errors;
      if (backendErrors) {
        Object.entries(backendErrors).forEach(([field, messages]) => {
          passwordForm.setError(field, {
            type: "server",
            message: messages[0],
          });
        });
      } else {
        toast.error("Failed to change password");
      }
    } finally {
      setLoadingPassword(false);
    }
  };

  // Change PIN (used to switch from student portal to parent portal)
  const onChangePin = async (data) => {
    setLoadingPin(true);
    try {
      const payload = {
        current_pin: data.current_pin,
        new_pin: data.new_pin,
        new_pin_confirmation: data.new_pin_confirmation,
      };
      await api.post("parent/pin/change", payload);
      toast.success("PIN changed successfully!");
      pinForm.reset({
        current_pin: "",
        new_pin: "",
        new_pin_confirmation: "",
      });
    } catch (err) {
      const backendErrors = err.response?.data?.errors;
      if (backendErrors) {
        Object.entries(backendErrors).forEach(([field, messages]) => {
          pinForm.setError(field, {
            type: "server",
            message: messages[0],
          });
        });
      } else if (err.response?.status === 401) {
        toast.error(err.response?.data?.message || "Current PIN is incorrect");
      } else {
        toast.error("Failed to change PIN");
      }
    } finally {
      setLoadingPin(false);
    }
  };

  const pinRules = {
    required: "PIN is required",
    minLength: { value: 4, message: "PIN must be 4 digits" },
    maxLength: { value: 4, message: "PIN must be 4 digits" },
    pattern: { value: /^[0-9]{4}$/, message: "PIN must contain only digits (0-9)" },
  };

  const confirmPinRules = {
    ...pinRules,
    validate: (value) => value === pinForm.getValues("new_pin") || "PINs do not match",
  };

  const pinInputProps = { maxLength: 4, inputMode: "numeric", pattern: "[0-9]*" };

  const mustChangePassword = localStorage.getItem("mustChangePassword") === "1";

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Typography variant="h5" mb={3}>
        Settings
      </Typography>

      {mustChangePassword && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          You must change your password before continuing. This is required on your first login after registration.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left: Update Profile */}
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ height: "100%", borderColor: "divider" }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} mb={2}>
                Update Profile
              </Typography>
              <form onSubmit={profileForm.handleSubmit(onUpdateProfile)}>
                <Grid container spacing={2}>
                  <InputField
                    control={profileForm.control}
                    name="first_name"
                    label="First Name"
                    error={profileForm.formState.errors.first_name}
                    fullWidth
                  />
                  <InputField
                    control={profileForm.control}
                    name="last_name"
                    label="Last Name"
                    error={profileForm.formState.errors.last_name}
                    fullWidth
                  />
                  <SubmitButton loading={loadingProfile} label="Update Profile" />
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>

        {/* Middle: Change Password */}
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ height: "100%", borderColor: "divider" }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} mb={2}>
                Change Password
              </Typography>
              <form onSubmit={passwordForm.handleSubmit(onChangePassword)}>
                <Grid container spacing={2}>
                  <InputField
                    control={passwordForm.control}
                    name="current_password"
                    label="Current Password"
                    type="password"
                    error={passwordForm.formState.errors.current_password}
                    fullWidth
                  />
                  <InputField
                    control={passwordForm.control}
                    name="new_password"
                    label="New Password"
                    type="password"
                    error={passwordForm.formState.errors.new_password}
                    fullWidth
                  />
                  <InputField
                    control={passwordForm.control}
                    name="new_password_confirmation"
                    label="Confirm New Password"
                    type="password"
                    error={passwordForm.formState.errors.new_password_confirmation}
                    fullWidth
                  />
                  <SubmitButton loading={loadingPassword} label="Change Password" />
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>

        {/* Right: Change PIN */}
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ height: "100%", borderColor: "divider" }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} mb={0.5}>
                Change PIN
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Use this PIN to switch from student portal to parent portal. This option is only visible to parents.
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1.5 }}>
                PIN validation: exactly 4 digits (0–9 only).
              </Typography>
              <form onSubmit={pinForm.handleSubmit(onChangePin)}>
                <Grid container spacing={2}>
                  <InputField
                    control={pinForm.control}
                    name="current_pin"
                    label="Current PIN"
                    type="password"
                    error={pinForm.formState.errors.current_pin}
                    fullWidth
                    rules={pinRules}
                    inputProps={pinInputProps}
                  />
                  <InputField
                    control={pinForm.control}
                    name="new_pin"
                    label="New PIN"
                    type="password"
                    error={pinForm.formState.errors.new_pin}
                    fullWidth
                    rules={pinRules}
                    inputProps={pinInputProps}
                  />
                  <InputField
                    control={pinForm.control}
                    name="new_pin_confirmation"
                    label="Confirm New PIN"
                    type="password"
                    error={pinForm.formState.errors.new_pin_confirmation}
                    fullWidth
                    rules={confirmPinRules}
                    inputProps={pinInputProps}
                  />
                  <SubmitButton loading={loadingPin} label="Change PIN" />
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Setting;
